import { useState } from 'react'
import { Building2, Lock, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useFacilityMutations } from '@/hooks/useFacility'
import type { LocationTree } from '@/api/facilityApi'
import { FacilityForm } from './FacilityBar'

type Facility = LocationTree['facilities'][number]

/**
 * Which facility is shown, and adding or changing one (z8nrz7fw25). Used by
 * the facility set-up and by Storage → Locations, so a facility can be added
 * or renamed from either.
 *
 * Add and Edit open their own modal dialog rather than a popover: the switcher
 * often sits inside the set-up window, and a popover there opened behind it,
 * where it could not be reached. A dialog opened from a dialog stacks on top.
 */
export function FacilitySwitcher({
  facilities,
  selected,
  onSelect,
  editable,
  run,
  className,
}: {
  facilities: Facility[]
  selected: Facility | null
  onSelect: (id: string) => void
  /** `equipment.manage`. Without it the switcher only switches, and says why. */
  editable: boolean
  run: (fn: () => Promise<unknown>) => Promise<void>
  className?: string
}) {
  const mut = useFacilityMutations()
  const [form, setForm] = useState<'add' | 'edit' | null>(null)
  const editing = form === 'edit' ? selected : null

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <span className='flex items-center gap-1.5 text-sm font-medium text-slate-600'>
        <Building2 className='h-4 w-4' /> Facility
      </span>
      <div role='tablist' aria-label='Facility' className='inline-flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1'>
        {facilities.map((f) => (
          <button
            key={f.id}
            type='button'
            role='tab'
            aria-selected={f.id === selected?.id}
            onClick={() => onSelect(f.id)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              f.id === selected?.id ? 'bg-white font-medium text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
            )}
          >
            <span className='font-bold'>{f.code}</span>
            <span className='hidden lg:inline'>{f.name}</span>
          </button>
        ))}
      </div>

      {editable ? (
        <>
          {selected && (
            <Button variant='outline' size='sm' className='h-8 gap-1' aria-label={`Edit ${selected.name}`} onClick={() => setForm('edit')}>
              <Pencil className='h-3.5 w-3.5' /> Edit
            </Button>
          )}
          <Button variant='outline' size='sm' className='h-8 gap-1 border-primary/40 text-primary hover:bg-primary/5' onClick={() => setForm('add')}>
            <Plus className='h-3.5 w-3.5' /> Add facility
          </Button>
        </>
      ) : (
        <span className='flex items-center gap-1 text-xs text-slate-500'>
          <Lock className='h-3 w-3' /> View only: you can look around but not change the facility.
        </span>
      )}

      <Dialog open={form !== null} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add a facility'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Its code leads every location path.' : 'A site you run. Buildings, floors and rooms go inside it.'}
            </DialogDescription>
          </DialogHeader>
          {form && (
            <FacilityForm
              key={form + (editing?.id ?? '')}
              initial={editing ?? undefined}
              submitLabel={editing ? 'Save facility' : 'Add facility'}
              onCancel={() => setForm(null)}
              onSubmit={(v) =>
                run(async () => {
                  if (editing) await mut.updateFacility.mutateAsync({ id: editing.id, ...v })
                  else onSelect((await mut.createFacility.mutateAsync(v)).id)
                  setForm(null)
                })
              }
            />
          )}
          {editing && editing.buildings.length === 0 && (
            <button
              type='button'
              className='flex items-center gap-1.5 text-xs text-destructive hover:underline'
              onClick={() => {
                if (window.confirm(`Delete ${editing.name}?`)) void run(() => mut.deleteFacility.mutateAsync(editing.id))
                setForm(null)
              }}
            >
              <Trash2 className='h-3.5 w-3.5' /> Delete this facility
            </button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
